<template>
  <v-card>
    <v-toolbar color="secondary" dark tabs>
      <v-toolbar-title>{{$t('design.contentBrowser')}}</v-toolbar-title>
      <v-tabs
        v-model="tabs"
        centered
        color="secondary"
        slot="extension"
        slider-color="accent"
      >
        <v-tab
          v-if="$store.getters.hasPermission('edit_layout_widgets')"
          href="#tab-widgets"
          ripple
        >
          {{$tc('design.widget', 2)}}
        </v-tab>
        <v-tab
          v-if="$store.getters.hasPermission('edit_layout_library')"
          href="#tab-library"
          ripple
        >
          {{$t('design.library')}}
        </v-tab>
        <v-tab
          v-if="
            $store.getters.hasPermission('edit_layout_special_widgets') &&
            specialWidgets.length > 0"
          href="#tab-special-widgets"
          ripple
        >
          {{$t('design.special')}}
        </v-tab>
      </v-tabs>
    </v-toolbar>
    <v-card-text>
      <v-tabs-items v-model="tabs">
        <v-tab-item
          v-if="$store.getters.hasPermission('edit_layout_widgets')"
          value="tab-widgets"
        >
          <v-list>
            <drag
              v-for="(widget, index) in widgets"
              :key="index"
              @dragstart="dragStart(widget)"
              :transferData="{ isWidget: true }"
            >
              <v-list-tile @click="''">
                <v-list-tile-action>
                  <v-icon v-if="widget.icon">{{widget.icon}}</v-icon>
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title v-text="widget.title"></v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </drag>
          </v-list>
        </v-tab-item>
        <v-tab-item
          v-if="$store.getters.hasPermission('edit_layout_library')"
          value="tab-library"
        >
          <loading-overlay
            v-show="libraryIsLoading"
            :color="'accent'"
            :size="70"
            :width="5"
          ></loading-overlay>
          <v-list>
            <drag
              v-for="(item, index) in library"
              :key="index"
              @dragstart="dragStart(item)"
              :transferData="{ isWidget: true }"
            >
              <v-list-tile @click="''">
                <v-list-tile-action>
                  <template
                    v-if="
                      item.hasOwnProperty('component') &&
                      item.component.localeCompare('LibraryImageWidget') == 0 &&
                      item.preview != null
                    "
                  >
                    <img class="library-preview-img" width="32" :src="item.preview" />
                  </template>
                  <template v-else>
                    <v-icon>{{item.icon}}</v-icon>
                  </template>

                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title v-text="item.name"></v-list-tile-title>
                  <v-list-tile-sub-title v-text="item.type"></v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </drag>
          </v-list>
        </v-tab-item>
        <v-tab-item
          v-if="
            $store.getters.hasPermission('edit_layout_special_widgets') &&
            specialWidgets.length > 0"
          value="tab-special-widgets"
        >
          <loading-overlay
            v-show="specialWidgetsIsLoading"
            :color="'accent'"
            :size="70"
            :width="5"
          ></loading-overlay>
          <v-list>
            <drag
              v-for="(item, index) in specialWidgets"
              :key="index"
              @dragstart="dragStart(item)"
              :transferData="{ isWidget: true }"
            >
              <v-list-tile @click="''">
                <v-list-tile-action>
                  <template
                    v-if="
                      item.hasOwnProperty('component') &&
                      item.preview != null
                    "
                  >
                    <img class="library-preview-img" width="32" :src="item.preview" />
                  </template>
                  <template v-else>
                    <v-icon>{{item.icon}}</v-icon>
                  </template>

                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title v-text="item.name"></v-list-tile-title>
                  <v-list-tile-sub-title v-text="item.type"></v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>
            </drag>
          </v-list>
        </v-tab-item>
      </v-tabs-items>
    </v-card-text>
  </v-card>
</template>

<script>
import axios from 'axios';
import { Drag } from 'vue-drag-drop';
import { SET_DRAGGED_WIDGET } from '@/store/actions/layouteditor';
import LoadingOverlay from '@/components/generic/LoadingOverlay.vue';

export default {
  components: {
    Drag,
    LoadingOverlay,
  },
  data() {
    return {
      widgets: [],
      library: [],
      specialWidgets: [],
      apiUrl: window.api_url,
      tabs: null,
      libraryIsLoading: true,
      specialWidgetsIsLoading: true,
    };
  },
  mounted() {
    this.checkWidgetsPermissions();
    this.getLibrary();
    this.getSpecialWidgets();
  },
  methods: {
    dragStart(widget) {
      this.$store.dispatch(SET_DRAGGED_WIDGET, widget);
    },
    checkWidgetsPermissions() {
      if (this.$store.getters.hasPermission('use_audio_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.audio'),
          name: 'audio',
          icon: 'audiotrack',
          component: 'AudioWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_clock_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.clock'),
          name: 'clock',
          icon: 'schedule',
          component: 'ClockWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_image_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.image'),
          name: 'image',
          icon: 'image',
          component: 'ImageWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_pdf_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.pdf'),
          name: 'pdf',
          icon: 'picture_as_pdf',
          component: 'PdfWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_text_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.text'),
          name: 'text',
          icon: 'text_fields',
          component: 'TextWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_ticker_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.ticker'),
          name: 'ticker',
          icon: 'call_to_action',
          component: 'TickerWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_video_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.video'),
          name: 'video',
          icon: 'movie',
          component: 'VideoWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_vnc_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.vncViewer'),
          name: 'vnc',
          icon: 'mdi-remote-desktop',
          component: 'VncWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_web_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.webContent'),
          name: 'webpage',
          icon: 'language',
          component: 'WebpageWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_youtube_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.youtube'),
          name: 'youtube',
          icon: 'mdi mdi-youtube',
          component: 'YoutubeWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_twitter_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.twitter'),
          name: 'twitter',
          icon: 'mdi mdi-twitter',
          component: 'TwitterWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_rss_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.rss'),
          name: 'rss',
          icon: 'mdi mdi-rss',
          component: 'RssWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_facebook_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.facebook'),
          name: 'facebook',
          icon: 'mdi mdi-facebook',
          component: 'FacebookWidget',
        });
      }

      if (this.$store.getters.hasPermission('use_magio_widget')) {
        this.widgets.push({
          title: this.$i18n.t('design.magio'),
          name: 'magio',
          icon: 'mdi mdi-television-box',
          component: 'MagioWidget',
        });
      }
    },
    getLibrary() {
      const vm = this;

      axios.get(`${this.apiUrl}api/media`, {
        params: {
          paginate: false,
        },
      })
        .then((response) => {
          vm.library = response.data.data;

          vm.library.forEach((widget) => {
            /* eslint-disable no-param-reassign */
            if (widget.type === 'audio') {
              widget.component = 'LibraryAudioWidget';
              widget.icon = 'audiotrack';
            } else if (widget.type === 'image') {
              widget.component = 'LibraryImageWidget';
              widget.icon = 'image';
            } else if (widget.type === 'video') {
              widget.component = 'LibraryVideoWidget';
              widget.icon = 'movie';
            } else if (widget.type === 'web') {
              widget.component = 'LibraryWebpageWidget';
              widget.icon = 'language';
            } else if (widget.type === 'pdf') {
              widget.component = 'LibraryPdfWidget';
              widget.icon = 'picture_as_pdf';
            }
            /* eslint-enable */
          });

          this.libraryIsLoading = false;
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });
    },
    getSpecialWidgets() {
      const vm = this;

      axios.get(`${this.apiUrl}api/widgets/special`, {
        params: {
          paginate: false,
        },
      })
        .then((response) => {
          vm.specialWidgets = response.data.data;

          vm.specialWidgets.forEach((specialWidget) => {
            /* eslint-disable no-param-reassign */
            if (specialWidget.type === 'audio') {
              specialWidget.component = 'LibraryAudioWidget';
              specialWidget.icon = 'audiotrack';
            } else if (specialWidget.type === 'image') {
              specialWidget.component = 'LibraryImageWidget';
              specialWidget.icon = 'image';
            } else if (specialWidget.type === 'video') {
              specialWidget.component = 'LibraryVideoWidget';
              specialWidget.icon = 'movie';
            } else if (specialWidget.type === 'web') {
              specialWidget.component = 'LibraryWebpageWidget';
              specialWidget.icon = 'language';
            } else if (specialWidget.type === 'pdf') {
              specialWidget.component = 'LibraryPdfWidget';
              specialWidget.icon = 'picture_as_pdf';
            }
            /* eslint-enable */
          });

          this.specialWidgetsIsLoading = false;
        })
        .catch((error) => {
          vm.$toasted.show(error.response.data.message, { duration: 3000, singleton: true, type: 'error' });
        });
    },
  },
};
</script>

<style>
.v-list__tile:hover {
  cursor: pointer;
}
</style>
